"use client";
import { HTMLProps, MouseEvent, useEffect, useMemo, useState } from "react";
import { useAppStore } from "@/store/app";
import { getLang } from "@/utils/selectors";
import { cn, createParamUrl, getLocationNameWithStateName, getLocationType } from "@/utils/helpers";
import { SvgIconDownload, SvgIconLink } from "@/components/common/Svg";
import Panel from "@/components/common/Panel";
import Button from "@/components/common/Button";
import Tooltip from "@/components/common/Tooltip";
import ProfileSmoke from "./components/ProfileSmoke";
import ProfileTemp from "./components/ProfileTemp";
import styles from "./Profile.module.css";
import { ControlsGeoType } from "@/utils/types";

export interface ProfileProps extends HTMLProps<HTMLElement> {}

export default function Profile({
	className
} : ProfileProps) {
	const [open, setOpen] = useState<boolean>(false);
	const [copied, setCopied] = useState<boolean>(false);
	const [copyUrl, setCopyUrl] = useState<string>("");
	const [downloadUrl, setDownloadUrl] = useState<string>("");
	const controls = useAppStore(store => store.controls);
	const selectedLocation = useAppStore(store => store.selectedLocation);
	const setSelectedLocation = useAppStore(store => store.setSelectedLocation);

	const locationGeo = useMemo(() =>
		selectedLocation && getLocationType(selectedLocation?.id)
	, [selectedLocation]) as ControlsGeoType;

	const copyToClipboard = async (text: string) => {
		await navigator.clipboard.writeText(text);
	};

	const handleCopy = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		copyToClipboard(copyUrl);
		setTimeout(() =>
			setCopied(true)
		, 0);
		setTimeout(() =>
			setCopied(false)
		, 2000);
		return false;
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedLocation(null);
	};

	const handleCloseComplete = () => {
		setSelectedLocation(null);
	}

	useEffect(() => {
		if(!selectedLocation?.id) return
		// Update copy URL to explorer URL with selected location param
		const copyUrlParams = { ...controls, id: selectedLocation?.id };
		const copyUrl = createParamUrl(copyUrlParams);
		setCopyUrl(copyUrl);
		// Update download URL to PDF of selected location
		setDownloadUrl(`https://storage.googleapis.com/location_pdfs/report_${selectedLocation?.id}.pdf`);
		setOpen(Boolean(selectedLocation));
	}, [controls, selectedLocation]);

	return (
		<Panel
			open={open}
			position="left"
			title={selectedLocation ? getLocationNameWithStateName(locationGeo, selectedLocation.id) : undefined}
			description={getLang("profile", controls.view, locationGeo)}
			tools={
				<>
					{locationGeo === "county" ?
						<Button
							before={<SvgIconDownload />}
							href={downloadUrl}
							target="_blank"
							rel="noopener noreferrer"
							className={styles.ProfileToolsDownload}
						>
							{getLang("profile", "download")}
						</Button>
					: null}
					<Tooltip
						open={copied}
						inner={getLang("profile", "copy", "tooltip")}
						className={styles.ProfileToolsCopy}
					>
						<Button
							before={<SvgIconLink />}
							href={copyUrl}
							onClick={handleCopy}
						>
							{getLang("profile", "copy")}
						</Button>
					</Tooltip>
				</>
			}
			onClose={handleClose}
			onBackdropClick={handleClose}
			onCloseComplete={handleCloseComplete}
			className={cn(
				styles.Profile,
				className
			)}
		>
			{controls.view === "smoke"
				? <ProfileSmoke />
				: <ProfileTemp />
			}
		</Panel>
	);
}